/**
 * Les QR codes de paiement des pays qui n'utilisent pas l'IBAN — le même
 * calcul que le serveur (backend/.../document/QrNationaux.java), pour que
 * l'écran montre le QR code que le client recevra.
 *
 * <p>Forme EMVCo (champs ID–longueur–valeur, CRC-16 en fin) pour Pix,
 * PromptPay, PayNow, VietQR et FPS ; adresse `upi://pay` pour l'UPI indien.
 */

/** Un champ EMVCo : identifiant, longueur sur deux chiffres, valeur. */
export const champ = (id, valeur) => id + String(valeur.length).padStart(2, '0') + valeur;

/** Somme CRC-16/CCITT-FALSE, sur les octets UTF-8. */
export function crc16(texte) {
  let crc = 0xffff;
  for (const b of new TextEncoder().encode(texte)) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
      crc &= 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

const avecCrc = (s) => s + '6304' + crc16(s + '6304');

const deuxDecimales = (m) => (Math.round(Number(m) * 100) / 100).toFixed(2);

/** Lettres sans accents, comme les applications bancaires les attendent. */
export function ascii(valeur, max) {
  const sans = String(valeur || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim();
  return sans.slice(0, max);
}

const encode = (v) => encodeURIComponent(v);

function upi(vpa, nom, montant, reference) {
  let uri = 'upi://pay?pa=' + encode(vpa) + '&pn=' + encode(nom || '');
  if (montant > 0) uri += '&am=' + deuxDecimales(montant);
  uri += '&cu=INR';
  if (reference && reference.trim()) uri += '&tn=' + encode(reference);
  return uri;
}

function pix(cle, nom, ville, montant, reference) {
  let s = champ('00', '01');
  s += champ('26', champ('00', 'br.gov.bcb.pix') + champ('01', cle));
  s += champ('52', '0000') + champ('53', '986');
  if (montant != null) s += champ('54', deuxDecimales(montant));
  s += champ('58', 'BR') + champ('59', ascii(nom, 25));
  s += champ('60', ascii(ville && ville.trim() ? ville : 'BRASIL', 15));
  const txid = String(reference || '').replace(/[^A-Za-z0-9]/g, '');
  s += champ('62', champ('05', txid ? txid.slice(0, 25) : '***'));
  return avecCrc(s);
}

function promptPay(numero, montant) {
  const chiffres = numero.replace(/\D/g, '');
  let type;
  let valeur;
  if (chiffres.length >= 13 && chiffres.length < 15) {
    type = '02';
    valeur = chiffres;
  } else if (chiffres.length >= 15) {
    type = '03';
    valeur = chiffres;
  } else {
    type = '01';
    const local = chiffres.startsWith('66') ? chiffres.slice(2) : chiffres.replace(/^0/, '');
    valeur = '0066' + local;
  }
  let s = champ('00', '01') + champ('01', montant != null ? '12' : '11');
  s += champ('29', champ('00', 'A000000677010111') + champ(type, valeur));
  s += champ('58', 'TH') + champ('53', '764');
  if (montant != null) s += champ('54', deuxDecimales(montant));
  return avecCrc(s);
}

function payNow(identifiant, nom, montant, reference) {
  const chiffres = identifiant.replace(/[\s-]/g, '');
  const mobile = /^\+?(65)?[89]\d{7}$/.test(chiffres);
  const valeur = mobile ? '+65' + chiffres.replace(/^\+?65/, '') : chiffres.toUpperCase();
  const compte =
    champ('00', 'SG.PAYNOW') +
    champ('01', mobile ? '0' : '2') +
    champ('02', valeur) +
    champ('03', montant != null ? '0' : '1');
  let s = champ('00', '01') + champ('01', '12') + champ('26', compte);
  s += champ('52', '0000') + champ('53', '702');
  if (montant != null) s += champ('54', deuxDecimales(montant));
  s += champ('58', 'SG') + champ('59', ascii(nom, 25)) + champ('60', 'Singapore');
  if (reference && reference.trim()) {
    s += champ('62', champ('01', reference.replace(/\s/g, '').slice(0, 25)));
  }
  return avecCrc(s);
}

function vietQr(bin, compte, montant, reference) {
  const code = String(bin || '').replace(/\D/g, '');
  if (!code) return null;
  const beneficiaire = champ('00', code) + champ('01', compte.replace(/\s/g, ''));
  const info = champ('00', 'A000000727') + champ('01', beneficiaire) + champ('02', 'QRIBFTTA');
  let s = champ('00', '01') + champ('01', montant != null ? '12' : '11') + champ('38', info);
  s += champ('53', '704');
  // Le dong n'a pas de décimales.
  if (montant != null) s += champ('54', String(Math.round(Number(montant))));
  s += champ('58', 'VN');
  if (reference && reference.trim()) s += champ('62', champ('08', ascii(reference, 25)));
  return avecCrc(s);
}

function fps(identifiant, nom, montant, reference) {
  const id = identifiant.trim();
  let compte;
  if (id.includes('@')) compte = champ('04', id);
  else if (id.startsWith('+') || id.replace(/\D/g, '').length === 8) {
    compte = champ('03', '+852-' + id.replace(/\D/g, '').replace(/^852/, ''));
  } else compte = champ('02', id.replace(/\D/g, ''));
  let s = champ('00', '01') + champ('01', montant != null ? '12' : '11');
  s += champ('26', champ('00', 'hk.com.hkicl') + compte);
  s += champ('52', '0000') + champ('53', '344');
  if (montant != null) s += champ('54', deuxDecimales(montant));
  s += champ('58', 'HK') + champ('59', ascii(nom, 25)) + champ('60', 'HK');
  if (reference && reference.trim()) {
    s += champ('62', champ('01', reference.replace(/\s/g, '').slice(0, 25)));
  }
  return avecCrc(s);
}

/**
 * Le contenu du QR code d'un pays sans IBAN.
 *
 * @param {object} p
 * @param {string} p.systeme UPI, PIX, PROMPTPAY, PAYNOW, VIETQR ou FPS
 * @param {string} p.identifiant identifiant de paiement saisi par l'entreprise
 * @param {string} [p.banque] code de la banque (VietQR)
 * @param {string} p.nom bénéficiaire
 * @param {string} [p.ville] localité du bénéficiaire
 * @param {number} [p.montant] montant à payer
 * @param {string} [p.reference] référence de la pièce
 * @returns {string|null} le contenu à encoder, null si l'identifiant manque
 */
export function qrNational({ systeme, identifiant, banque, nom, ville, montant, reference }) {
  const id = String(identifiant || '').trim();
  if (!id) return null;
  const m = Number(montant) > 0 ? Number(montant) : null;
  switch (systeme) {
    case 'UPI':
      return upi(id, nom, m, reference);
    case 'PIX':
      return pix(id, nom, ville, m, reference);
    case 'PROMPTPAY':
      return promptPay(id, m);
    case 'PAYNOW':
      return payNow(id, nom, m, reference);
    case 'VIETQR':
      return vietQr(banque, id, m, reference);
    case 'FPS':
      return fps(id, nom, m, reference);
    default:
      return null;
  }
}

/** Une valeur SPAYD : l'astérisque y sépare les champs. */
const spayd = (v, max) =>
  String(v || '')
    .replace(/\*/g, ' ')
    .trim()
    .slice(0, max)
    .trim();

/**
 * QR Platba (Tchéquie, SPAYD 1.0), calculé depuis l'IBAN — même contenu que
 * le serveur (QrDepuisIban.java).
 *
 * @param {object} p
 * @param {string} p.iban compte du bénéficiaire
 * @param {string} p.nom bénéficiaire
 * @param {number} [p.montant] montant à payer
 * @param {string} [p.devise] devise, CZK par défaut
 * @param {string} [p.reference] référence : ses chiffres forment le symbole variable
 * @param {string} [p.message] message pour le bénéficiaire
 * @returns {string} le contenu à encoder
 */
export function qrPlatba({ iban, nom, montant, devise, reference, message }) {
  let s =
    'SPD*1.0*ACC:' +
    String(iban || '')
      .replace(/\s/g, '')
      .toUpperCase();
  if (Number(montant) > 0) s += '*AM:' + deuxDecimales(montant);
  // La devise réglée peut être un symbole (« Kč ») : seul un code ISO à trois
  // lettres est admis, la couronne sinon.
  const code = String(devise || '')
    .trim()
    .toUpperCase();
  s += '*CC:' + (/^[A-Z]{3}$/.test(code) ? code : 'CZK');
  const vs = String(reference || '').replace(/\D/g, '');
  if (vs) s += '*X-VS:' + vs.slice(-10);
  const rn = spayd(nom, 35);
  if (rn) s += '*RN:' + rn;
  const msg = spayd(message, 60);
  if (msg) s += '*MSG:' + msg;
  return s;
}

const sansBarre = (v, max) =>
  String(v || '')
    .replace(/\|/g, ' ')
    .trim()
    .slice(0, max)
    .trim();

/**
 * QR code de virement polonais (recommandation ZBP), calculé depuis l'IBAN —
 * même contenu que le serveur.
 *
 * @param {object} p
 * @param {string} p.iban compte du bénéficiaire (IBAN polonais)
 * @param {string} p.nom bénéficiaire
 * @param {number} [p.montant] montant ; au-delà de 9 999,99, laissé à saisir
 * @param {string} [p.titre] titre du virement
 * @returns {string|null} le contenu, null si le compte n'est pas polonais
 */
export function qrZbp({ iban, nom, montant, titre }) {
  let compte = String(iban || '')
    .replace(/\s/g, '')
    .toUpperCase();
  if (compte.startsWith('PL')) compte = compte.slice(2);
  if (!/^\d{26}$/.test(compte)) return null;
  const m = Number(montant);
  const grosze = m > 0 && m <= 9999.99 ? String(Math.round(m * 100)).padStart(6, '0') : '';
  return ['', 'PL', compte, grosze, sansBarre(nom, 20), sansBarre(titre, 32), '', '', ''].join('|');
}
